import React, { useState, useRef } from 'react';

import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import ListGroup from 'react-bootstrap/ListGroup';
import { ToastContainer, Toast, Alert } from 'react-bootstrap';
import { uploadFiles } from '../../apis/fileApi';

export function FilesUpload({ show, handleCloseModal, jobId, refreshFiles }) {
    const [filesInfo, setFilesInfo] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, title: '', message: '', variant: 'success' });
    const fileInputRef = useRef(null); // ref to reset file input display

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (selectedFiles.length === 0) return;
        if (selectedFiles.length + filesInfo.length > 5)
        {
            setErrorMessage('You can only upload up to 5 files at a time.');
            return;
        }

        var newSelectedFileNames = selectedFiles.map((file) => file.name)
    
        const mergedFileInfo =[...filesInfo.filter((file) => !newSelectedFileNames.includes(file.name)), ...selectedFiles.map((file) => ({ file:file, name: file.name, status: 'Pending', message: '' }))]

        setFilesInfo(mergedFileInfo);
        setErrorMessage('');
    };

    const handleRemoveFile = (fileToRemove) => {
        setFilesInfo((prevFiles) => {
            const indexToRemove = prevFiles.indexOf(fileToRemove);
            return prevFiles.filter((_, idx) => idx !== indexToRemove);
        });
    };

    const updateStatus = (index, status) => {
        const selectedFileInfo = filesInfo[index];
        selectedFileInfo.status = status;
        filesInfo[index] = selectedFileInfo;
        setFilesInfo([...filesInfo]);
        
    };

    const updateMessage = (index, message) => {
        const selectedFileInfo = filesInfo[index];
        selectedFileInfo.message = message;
        filesInfo[index] = selectedFileInfo;
        setFilesInfo([...filesInfo]);
    };

    // Reset all state including file input display
    const resetAll = () => {
        setFilesInfo([]);
        setErrorMessage('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSave = async () => {
        if (!filesInfo || filesInfo.length === 0) {
            setErrorMessage('Please select at least one file to upload.');
            return;
        }

        setErrorMessage('');
        setIsLoading(true);

        const uploadItems = filesInfo.map((fileInfo) => {
            return {name: fileInfo.name, type: fileInfo.file.type, size: fileInfo.file.size};
        });

        try {
            const results = await uploadFiles(uploadItems, updateStatus, updateMessage, jobId);
            const errors = results.filter((item) => item.status !== 'success');
            if (errors.length === 0) {
                setToast({ show: true, title: 'Upload complete', message: 'All files uploaded successfully.', variant: 'success' });
                resetAll(); // clear everything on full success
                if (refreshFiles) refreshFiles();
            } else {
                setToast({ show: true, title: 'Upload completed', message: `${errors.length} file(s) failed to upload.`, variant: 'danger' });
            }
        } catch (error) {
            setToast({ show: true, title: 'Upload failed', message: error?.message || 'Unexpected upload error.', variant: 'danger' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        resetAll();
        handleCloseModal();
    }
    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Upload Files for Job ID: {jobId}</Modal.Title>
            </Modal.Header>

                <Modal.Body>
                    <Form.Group controlId="formFileMultiple" className="mb-3">
                        <Form.Label>Select files to upload</Form.Label>
                            <div className="d-flex align-items-center gap-2 border border-solid rounded">
                                    <Form.Control
                                            type="file"
                                            multiple
                                            onChange={handleFileChange}
                                            ref={fileInputRef}
                                            style={{ display: 'none', }}
                                    />
                                    <Button variant="light" size="sm" onClick={() => fileInputRef.current?.click()}>
                                            Choose Files
                                    </Button>
                                    <span className="text-muted">
                                            {filesInfo.length === 0 ? 'No files chosen' : `${filesInfo.length} file${filesInfo.length > 1 ? 's' : ''} chosen`}
                                    </span>
                            </div>
                    </Form.Group>

                {errorMessage && <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>{errorMessage}</Alert>}

                <ListGroup>
                    {filesInfo.map((fileInfo, i) => (
                        <ListGroup.Item key={i} className="d-flex justify-content-between align-items-center">
                            <div>
                                <div className="fw-semibold">{fileInfo.name}</div>
                                <div className="text-muted">{Math.round((fileInfo.file.size || 0) / 1024)} KB</div>
                                {fileInfo.status && (
                                    <span className={`badge bg-${
                                        fileInfo.status === 'uploadComplete' ? 'success' :
                                        fileInfo.status === 'error' ? 'danger' : 'warning'
                                    } me-2`}>
                                        {fileInfo.status}
                                    </span>
                                )}
                                {fileInfo.message && (
                                    <div className="small text-muted">{fileInfo.message}</div>
                                )}
                            </div>
                            <div>
                                <Button variant="outline-danger" size="sm" onClick={() => handleRemoveFile(fileInfo)}>Remove</Button>
                            </div>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>Close</Button>
                <Button variant="primary" onClick={handleSave} disabled={isLoading}>{isLoading ? 'Uploading...' : 'Upload Files'}</Button>
            </Modal.Footer>

            <ToastContainer className="p-3 position-absolute bottom-0 end-0">
                <Toast show={toast.show} bg={toast.variant} onClose={() => setToast({ ...toast, show: false })} delay={4000} autohide>
                    <Toast.Header>
                        <strong className="me-auto">{toast.title}</strong>
                    </Toast.Header>
                    <Toast.Body className={toast.variant === 'danger' ? 'text-white' : ''}>{toast.message}</Toast.Body>
                </Toast>
            </ToastContainer>
        </Modal>
    );
}
